import React, { useCallback } from "react"
import { View, ViewProps } from "react-native"

import styled from "styled-components/native"

import { Track } from "../lib/audio-player"
import { kebabCase } from "../lib/kebab-case"
import Row from "./row"
import Text from "./text"
import Touchable from "./touchable"

type TitleAlignment = "left" | "center"

// flex: 1 is required to get text truncation
// with ellipses working on the web
const TitleLinesView = styled(View)<{ $itemAlignment: TitleAlignment }>`
  flex: 1;
  align-items: ${({ $itemAlignment: itemAlignment }) => itemAlignment};
`

const TitleContainer = styled(View)`
  width: 100%;
`

export default function Title({
  track,
  onPress,
  style,
  alignment = "left",
}: {
  track: Track
  onPress?: (track: Track) => void
  style?: ViewProps["style"]
  alignment?: TitleAlignment
}): React.JSX.Element | null {
  const onPressCallback = useCallback(() => {
    if (onPress) {
      onPress(track)
    }
  }, [onPress, track])

  // If we weren't given any non-empty lines then don't render anything
  const lines = [track.title, track.album].filter(Boolean)
  if (lines.length === 0) {
    return null
  }

  // Separate out the first line and render it in bold.
  const firstLine = (
    <Text textStyle="headingSmall" numberOfLines={1}>
      {lines[0]}
    </Text>
  )
  const otherLines = lines.slice(1).map((line) => (
    <Text key={line} textStyle="caption" numberOfLines={1} color="secondary">
      {line}
    </Text>
  ))

  // Return a touchable if props.onPress was provided
  return (
    <TitleContainer style={style}>
      {onPress ? (
        <Touchable
          id={kebabCase(
            `audio-player-title-${track.album || "unknown-album"}-${track.title}`,
          )}
          onPress={onPressCallback}
          role="link"
          aria-label={lines[0]}
        >
          <Row>
            <TitleLinesView $itemAlignment={alignment}>
              {firstLine}
              {otherLines}
            </TitleLinesView>
          </Row>
        </Touchable>
      ) : (
        <>
          {firstLine}
          {otherLines}
        </>
      )}
    </TitleContainer>
  )
}
